import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Eye, EyeOff, RefreshCw, Power, ClipboardPaste, Check } from 'lucide-react';
import { getAppCursorRules, provisionMcpKey, toggleAppMcp, rotateAppMcpKey, revokeAppKey } from '../api';
import { useAuthStore } from '@/store/authStore';
import type { AppKey } from '../types';
import {
  PREVIEW_SETUP_PROMPT,
  SETUP_PROMPT_LOAD_ERROR,
  buildMcpJson,
  cursorConnectionName,
  injectMcpKey,
  type CursorRulesLoadState,
} from './mcpSetupPrompt';

export type McpCardPreviewState = 'fresh' | 'previous-active' | 'after-revoke';

interface McpConnectionCardProps {
  appId: string;
  appName: string;
  mcpSettings?: {
    enabled?: boolean;
    endpoint_created_at?: string;
    disabled_reason?: string | null;
  };
  mcpKeyId?: string | null;
  keys?: AppKey[];
  previewState?: McpCardPreviewState;
  onRefresh?: () => void;
  onMcpKeyChange?: (key: string | null) => void;
}

function isMcpKeyRow(k: AppKey): boolean {
  return String(k.key_type || '').toLowerCase() === 'mcp' || /mcp/i.test(String(k.name || ''));
}

export function McpConnectionCard({ appId, appName, mcpSettings, mcpKeyId, keys = [], previewState, onRefresh, onMcpKeyChange }: McpConnectionCardProps) {
  const [showKey, setShowKey] = useState(false);
  const [mcpKey, setMcpKey] = useState<string | null>(previewState ? 'mcp_<session-only-not-retrievable>' : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showSetup, setShowSetup] = useState(true);
  const [cursorRulesLoadState, setCursorRulesLoadState] = useState<CursorRulesLoadState>(
    previewState ? 'ready' : 'loading',
  );
  const [serverMcpJson, setServerMcpJson] = useState<string | null>(null);
  const [serverSetupPrompt, setServerSetupPrompt] = useState<string | null>(
    previewState ? PREVIEW_SETUP_PROMPT : null,
  );
  const [cursorRulesError, setCursorRulesError] = useState<string | null>(null);
  const [cursorRulesReloadNonce, setCursorRulesReloadNonce] = useState(0);
  const [sessionPrevious, setSessionPrevious] = useState<Array<{ key_id: string; lookup_hmac_prefix12?: string | null; superseded_by?: string | null }>>(
    previewState === 'previous-active'
      ? [{ key_id: 'key_previousExample', lookup_hmac_prefix12: 'a1b2c3d4e5f6', superseded_by: 'key_newExample' }]
      : [],
  );
  const [revokedPreviousIds, setRevokedPreviousIds] = useState<string[]>(
    previewState === 'after-revoke' ? ['key_previousExample'] : [],
  );
  const authToken = useAuthStore((s) => s.token);
  const selectedTenant = useAuthStore((s) => s.selectedTenant);

  React.useEffect(() => {
    onMcpKeyChange?.(mcpKey);
  }, [mcpKey, onMcpKeyChange]);

  const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://devcockpit.ai';
  const endpoint = `${baseOrigin}/mcp/app/${appId}`;
  const isProvisioned = !!mcpKeyId || !!mcpSettings?.endpoint_created_at;
  const isEnabled = mcpSettings?.enabled !== false;

  const setupPromptReady = cursorRulesLoadState === 'ready'
    && typeof serverSetupPrompt === 'string'
    && serverSetupPrompt.length > 0;
  const setupPromptPlaceholder = setupPromptReady ? injectMcpKey(serverSetupPrompt, null) : null;
  const mcpJsonReady = cursorRulesLoadState === 'ready';
  const placeholderConfig = mcpJsonReady
    ? (injectMcpKey(serverMcpJson, null) || buildMcpJson(appId, endpoint, null, appName))
    : null;
  const connectionName = cursorConnectionName(appName);
  const connectionEntry = buildMcpJson(appId, endpoint, mcpKey, appName);

  const endpointCreatedAt = mcpSettings?.endpoint_created_at ? new Date(mcpSettings.endpoint_created_at) : null;
  const keyAgeDays = endpointCreatedAt ? Math.floor((Date.now() - endpointCreatedAt.getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const showRotationReminder = Boolean(endpointCreatedAt && keyAgeDays >= 7) && !previewState;

  const activeMcpKeys = (keys || []).filter((k) => isMcpKeyRow(k) && String(k.status || '').toLowerCase() === 'active');
  const previousFromRegistry = activeMcpKeys.filter((k) => k.superseded_by);
  const previousKeys = sessionPrevious.length
    ? sessionPrevious
    : previousFromRegistry.map((k) => ({
      key_id: k.key_id,
      lookup_hmac_prefix12: null,
      superseded_by: k.superseded_by || null,
    }));
  const visiblePrevious = previousKeys.filter((k) => !revokedPreviousIds.includes(k.key_id));

  React.useEffect(() => {
    if (!appId || previewState) return undefined;

    let cancelled = false;

    async function loadContext() {
      setCursorRulesLoadState('loading');
      setCursorRulesError(null);
      try {
        const data = await getAppCursorRules(appId);
        if (cancelled) return;
        const prompt = typeof data?.setup_prompt === 'string' && data.setup_prompt.trim()
          ? data.setup_prompt
          : null;
        const mcpJson = typeof data?.mcp_json === 'string' && data.mcp_json.trim()
          ? data.mcp_json
          : null;
        setServerMcpJson(mcpJson);
        if (!prompt) {
          setServerSetupPrompt(null);
          setCursorRulesLoadState('error');
          setCursorRulesError(SETUP_PROMPT_LOAD_ERROR);
          return;
        }
        setServerSetupPrompt(prompt);
        setCursorRulesLoadState('ready');
      } catch {
        if (cancelled) return;
        setServerMcpJson(null);
        setServerSetupPrompt(null);
        setCursorRulesLoadState('error');
        setCursorRulesError(SETUP_PROMPT_LOAD_ERROR);
      }
    }

    void loadContext();

    return () => {
      cancelled = true;
    };
  }, [appId, authToken, selectedTenant, previewState, cursorRulesReloadNonce]);

  async function handleProvision() {
    setLoading(true);
    setError(null);
    try {
      const result = await provisionMcpKey(appId);
      if (result.key) setMcpKey(result.key);
      onRefresh?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle() {
    setLoading(true);
    setError(null);
    try {
      await toggleAppMcp(appId, !isEnabled, isEnabled ? 'Disabled from UI' : undefined);
      onRefresh?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRotate() {
    if (!confirm('Mint a new MCP key? The current key stays active until you deliberately revoke it. Existing Cursor sessions will not disconnect.')) return;
    setLoading(true);
    setError(null);
    try {
      const result = await rotateAppMcpKey(appId);
      if (result.new_key || result.key) setMcpKey(result.new_key || result.key || null);
      if (Array.isArray(result.previous_keys_still_active)) {
        setSessionPrevious(result.previous_keys_still_active.map((k) => ({
          key_id: k.key_id,
          lookup_hmac_prefix12: k.lookup_hmac_prefix12,
          superseded_by: k.superseded_by,
        })));
      }
      onRefresh?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRevokePrevious(keyId: string) {
    if (!confirm('Revoke this previous MCP key? Any client still using it will be disconnected and get 401. The newly minted key stays active. This cannot be undone.')) return;
    setLoading(true);
    setError(null);
    try {
      await revokeAppKey(appId, keyId);
      setRevokedPreviousIds((ids) => [...ids, keyId]);
      setSessionPrevious((rows) => rows.filter((r) => r.key_id !== keyId));
      onRefresh?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2500);
  }

  function copyServerSetupPrompt(label: string) {
    if (!setupPromptReady || !setupPromptPlaceholder) return;
    copyToClipboard(setupPromptPlaceholder, label);
  }

  const setupCopyDisabled = !setupPromptReady;
  const setupCopyLabel = cursorRulesLoadState === 'loading'
    ? 'Loading setup prompt…'
    : copied === 'setup-prompt'
      ? 'Copied!'
      : 'Copy setup prompt (placeholder key)';

  if (!isProvisioned) {
    return (
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold text-sm mb-2">MCP Connection (Cursor IDE)</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Provision an MCP endpoint so Cursor can connect to this app's agent ecosystem.
          </p>
          <Button size="sm" onClick={handleProvision} disabled={loading}>
            {loading ? 'Provisioning...' : 'Provision MCP Endpoint'}
          </Button>
          {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">MCP Connection (Cursor IDE)</h3>
          <Badge variant={isEnabled ? 'default' : 'destructive'}>
            {isEnabled ? 'Active' : 'Disabled'}
          </Badge>
        </div>

        <div>
          <label className="text-xs text-muted-foreground">Endpoint</label>
          <div className="flex items-center gap-1 mt-0.5">
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">{endpoint}</code>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(endpoint, 'endpoint')}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div data-testid="mcp-connection-entry" className="rounded-md border border-border p-2 space-y-1">
          <p className="text-xs font-medium">Cursor connection</p>
          <p className="text-xs">Name: <code>{connectionName}</code></p>
          <p className="text-xs break-all">Address: <code>{endpoint}</code></p>
          <p className="text-xs">Header: <code>Authorization</code></p>
          <p className="text-xs text-muted-foreground">
            One connection per app. Tick only this one. Put the key in this entry&apos;s header only, never in Cursor Secrets.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            data-testid="mcp-copy-connection"
            onClick={() => copyToClipboard(connectionEntry, 'connection')}
          >
            {copied === 'connection' ? <Check className="h-3 w-3 mr-1.5" /> : <Copy className="h-3 w-3 mr-1.5" />}
            {copied === 'connection' ? 'Copied!' : (mcpKey ? 'Copy connection entry with this key' : 'Copy connection entry')}
          </Button>
        </div>

        {mcpKey && (
          <div>
            <label className="text-xs text-muted-foreground">MCP Key (shown once)</label>
            <div className="flex items-center gap-1 mt-0.5">
              <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
                {showKey ? mcpKey : mcpKey.slice(0, 8) + '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
              </code>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowKey(!showKey)}>
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(mcpKey, 'key')}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        {showRotationReminder && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            This MCP setup is {keyAgeDays} days old. You may mint a new key if you want a fresh secret — the current key stays active until you deliberately revoke it.
          </div>
        )}

        <div
          data-testid="mcp-card-state"
          data-state={visiblePrevious.length ? 'previous-active' : revokedPreviousIds.length ? 'after-revoke' : (mcpKey ? 'fresh' : 'provisioned')}
          data-cursor-rules-state={cursorRulesLoadState}
          className="space-y-1.5"
        >
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            data-testid="mcp-copy-setup-prompt"
            disabled={setupCopyDisabled}
            onClick={() => copyServerSetupPrompt('setup-prompt')}
          >
            {copied === 'setup-prompt' ? <Check className="h-3 w-3 mr-1.5" /> : <ClipboardPaste className="h-3 w-3 mr-1.5" />}
            {setupCopyLabel}
          </Button>
          <p className="text-xs text-muted-foreground">
            The setup prompt does not contain the key. The key goes in the connection entry below, once, and never into Cursor Secrets.
          </p>
          {cursorRulesLoadState === 'error' && (
            <div data-testid="mcp-setup-prompt-error" className="rounded-md border border-red-300 bg-red-50 p-2 space-y-2">
              <p className="text-xs text-red-800">{cursorRulesError || SETUP_PROMPT_LOAD_ERROR}</p>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7"
                data-testid="mcp-setup-prompt-retry"
                onClick={() => setCursorRulesReloadNonce((n) => n + 1)}
              >
                Retry
              </Button>
            </div>
          )}
        </div>

        <details className="text-xs" open={showSetup} onToggle={(e) => setShowSetup((e.target as HTMLDetailsElement).open)}>
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-medium">
            Setup Instructions (single paste)
          </summary>
          <div className="mt-2 space-y-2 bg-muted/50 p-3 rounded-md">
            <p className="font-medium text-foreground">Use one paste into Cursor composer to create everything.</p>
            <div className="pt-1 border-t border-border">
              <p className="font-medium text-foreground mb-1">Then:</p>
              <ol className="list-decimal list-inside space-y-0.5 text-muted-foreground">
                <li>Open a new Cursor chat/composer in the target project.</li>
                <li>Paste the copied prompt and run it once.</li>
                <li>Restart Cursor (close + reopen project) to re-initialize MCP.</li>
                <li>Run <code className="text-[10px]">dc__describe_platform</code> to validate the connection.</li>
              </ol>
            </div>
          </div>
        </details>

        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Show mcp.json snippet</summary>
          <pre
            data-testid="mcp-json-snippet"
            className="bg-muted p-2 rounded mt-1 overflow-x-auto text-[10px] leading-4"
          >
            {cursorRulesLoadState === 'loading'
              ? 'Loading setup prompt…'
              : cursorRulesLoadState === 'error'
                ? SETUP_PROMPT_LOAD_ERROR
                : (placeholderConfig || 'Loading setup prompt…')}
          </pre>
          <p className="mt-1 text-muted-foreground">
            Placeholder only. The live key belongs in the connection entry above, and never in Cursor Secrets.
          </p>
        </details>

        {visiblePrevious.length > 0 && (
          <div data-testid="mcp-previous-keys" className="rounded-md border border-amber-400 bg-amber-50 p-2 space-y-2">
            <p className="text-xs font-medium text-amber-950">Previous key(s) still active</p>
            <p className="text-xs text-amber-900">
              Rotation minted a new key and did not disconnect anyone. These keys stay valid until you revoke them.
            </p>
            {visiblePrevious.map((k) => (
              <div key={k.key_id} className="flex items-center justify-between gap-2">
                <code className="text-[10px] truncate">{k.key_id}{k.lookup_hmac_prefix12 ? ` · ${k.lookup_hmac_prefix12}` : ''}</code>
                <Button
                  variant="destructive"
                  size="sm"
                  className="text-xs h-7"
                  disabled={loading || Boolean(previewState)}
                  onClick={() => handleRevokePrevious(k.key_id)}
                >
                  Revoke previous key
                </Button>
              </div>
            ))}
          </div>
        )}

        {revokedPreviousIds.length > 0 && visiblePrevious.length === 0 && (
          <div data-testid="mcp-after-revoke" className="rounded-md border border-slate-300 bg-slate-50 p-2 text-xs text-slate-800">
            Previous key revoked. Clients still on that key are disconnected. The current key remains active.
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" className="text-xs" onClick={handleToggle} disabled={loading || Boolean(previewState)}>
            <Power className="h-3 w-3 mr-1" />
            {isEnabled ? 'Disable' : 'Enable'}
          </Button>
          <Button variant="outline" size="sm" className="text-xs" onClick={handleRotate} disabled={loading || Boolean(previewState)}>
            <RefreshCw className="h-3 w-3 mr-1" />
            Make a new key
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          The old key keeps working until you revoke it. Paste the new key, fully quit and reopen Cursor, call dc__app_status, then revoke the old key.
        </p>

        {copied && !['config', 'rules'].includes(copied) && (
          <p className="text-xs text-green-600">Copied {copied} to clipboard</p>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        {mcpSettings?.disabled_reason && !isEnabled && (
          <p className="text-xs text-amber-600">Reason: {mcpSettings.disabled_reason}</p>
        )}
        <span className="sr-only">{appName}</span>
      </CardContent>
    </Card>
  );
}
